import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCreatePromotion } from "./api/promotionHooks";
import { useAuthStore } from "@/lib/store/authStore";
import { DocTypeSearchAutocomplete } from "@/components/search/DocTypeSearchAutocomplete";
import type { CreatePromotionInput } from "./api/promotionTypes";

function generateCode(name: string): string {
  const words = name.trim().toUpperCase().replace(/[^A-Z0-9\s]/g, "").split(/\s+/);
  const base = words.slice(0, 2).join("").slice(0, 8);
  const num = Math.floor(Math.random() * 90) + 10;
  return base ? `${base}${num}` : "";
}

function formatDateForInput(date: Date): string {
  return date.toISOString().split("T")[0];
}

export function CreatePromotionPage() {
  const navigate = useNavigate();
  const createMutation = useCreatePromotion();
  const tenant = useAuthStore((s) => s.currentTenant);

  const today = formatDateForInput(new Date());
  const thirtyDaysLater = formatDateForInput(
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
  );

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [code, setCode] = useState("");
  const [codeGenerated, setCodeGenerated] = useState(false);
  const [discountType, setDiscountType] = useState<"Amount" | "Percentage">("Amount");
  const [discountValue, setDiscountValue] = useState("");
  const [minimumOrder, setMinimumOrder] = useState("");
  const [visibility, setVisibility] = useState<"public" | "exclusive">("public");
  const [collectionLimit, setCollectionLimit] = useState("");
  const [minCartQty, setMinCartQty] = useState("");
  const [validFrom, setValidFrom] = useState(today);
  const [validUpto, setValidUpto] = useState(thirtyDaysLater);
  const [targetCustomerGroup, setTargetCustomerGroup] = useState("");
  const [codeCopied, setCodeCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [error, setError] = useState("");

  // Auto-generate code on title blur
  const handleTitleBlur = useCallback(() => {
    if (title && !code && !codeGenerated) {
      const generated = generateCode(title);
      setCode(generated);
      setCodeGenerated(true);
    }
  }, [title, code, codeGenerated]);

  // Check if required fields are filled (for Share section)
  const isFormValid =
    title.trim() &&
    code.trim() &&
    discountValue &&
    parseFloat(discountValue) > 0 &&
    validFrom &&
    validUpto;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!title.trim()) {
      setError("Promotion name is required");
      return;
    }
    if (!code.trim()) {
      setError("Coupon code is required");
      return;
    }
    if (!discountValue || parseFloat(discountValue) <= 0) {
      setError("Discount value must be greater than 0");
      return;
    }
    if (discountType === "Percentage" && parseFloat(discountValue) > 100) {
      setError("Percentage cannot exceed 100");
      return;
    }
    if (!validFrom || !validUpto) {
      setError("Both dates are required");
      return;
    }
    if (validUpto < validFrom) {
      setError("End date must be after start date");
      return;
    }

    const data: CreatePromotionInput = {
      promotion_title: title.trim(),
      coupon_code: code.trim().toUpperCase(),
      discount_type: discountType,
      discount_value: parseFloat(discountValue),
      minimum_order: minimumOrder ? parseFloat(minimumOrder) : undefined,
      is_public: visibility === "public" ? 1 : 0,
      max_collections: collectionLimit ? parseInt(collectionLimit, 10) : undefined,
      valid_from: validFrom,
      valid_upto: validUpto,
      promotion_description: description.trim() || undefined,
      minimum_cart_qty: minCartQty ? parseInt(minCartQty, 10) : undefined,
      custom_target_customer_group: targetCustomerGroup || undefined,
    };

    createMutation.mutate(data, {
      onError: (err) => {
        setError(err.message || "Failed to create promotion");
      },
    });
  };

  const storefrontUrl = tenant
    ? `https://${tenant}.shop.zaviago.com/coupons?code=${encodeURIComponent(code.trim().toUpperCase())}`
    : "";

  const copyToClipboard = async (text: string, type: "code" | "link") => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === "code") {
        setCodeCopied(true);
        setTimeout(() => setCodeCopied(false), 2000);
      } else {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      }
    } catch {
      // Fallback for non-HTTPS
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      if (type === "code") {
        setCodeCopied(true);
        setTimeout(() => setCodeCopied(false), 2000);
      } else {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      }
    }
  };

  return (
    <div className="mx-auto max-w-[640px] px-4 pt-12 pb-16">
      {/* Back link */}
      <button
        onClick={() => navigate("/promotions")}
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Promotions
      </button>

      <h1 className="mb-8 text-2xl font-semibold">Create Promotion</h1>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Section 1: Promotion Details */}
        <section>
          <h2 className="mb-4 text-sm font-semibold text-muted-foreground">
            PROMOTION DETAILS
          </h2>
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Promotion name *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={handleTitleBlur}
                placeholder="e.g., Summer Sale, New Customer Welcome"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => {
                  if (e.target.value.length <= 200) setDescription(e.target.value);
                }}
                placeholder="e.g., Valid on all bakery items. One per customer."
                className="mt-1.5"
                rows={3}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {description.length}/200 characters
              </p>
            </div>
            <div>
              <Label htmlFor="code">Coupon code *</Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""));
                  setCodeGenerated(false);
                }}
                placeholder="e.g., SUMMER50"
                className="mt-1.5 font-mono"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Uppercase letters, numbers, and hyphens only
              </p>
            </div>
          </div>
        </section>

        {/* Section 2: Discount */}
        <section>
          <h2 className="mb-4 text-sm font-semibold text-muted-foreground">
            DISCOUNT
          </h2>
          <div className="space-y-4">
            <div>
              <Label>Discount type</Label>
              <div className="mt-1.5 inline-flex rounded-sm border p-1">
                <button
                  type="button"
                  className={`rounded-xs px-4 py-1.5 text-sm font-medium transition-colors ${
                    discountType === "Amount"
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  onClick={() => setDiscountType("Amount")}
                >
                  Fixed Amount
                </button>
                <button
                  type="button"
                  className={`rounded-xs px-4 py-1.5 text-sm font-medium transition-colors ${
                    discountType === "Percentage"
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  onClick={() => setDiscountType("Percentage")}
                >
                  Percentage
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="discountValue">
                {discountType === "Amount" ? "Discount amount *" : "Discount percentage *"}
              </Label>
              <div className="relative mt-1.5">
                {discountType === "Amount" && (
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    ฿
                  </span>
                )}
                <Input
                  id="discountValue"
                  type="number"
                  min="0"
                  max={discountType === "Percentage" ? "100" : undefined}
                  step="any"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  className={discountType === "Amount" ? "pl-7" : ""}
                  placeholder={discountType === "Amount" ? "e.g., 100" : "e.g., 15"}
                />
                {discountType === "Percentage" && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    %
                  </span>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="minimumOrder">Minimum order amount</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  ฿
                </span>
                <Input
                  id="minimumOrder"
                  type="number"
                  min="0"
                  step="any"
                  value={minimumOrder}
                  onChange={(e) => setMinimumOrder(e.target.value)}
                  className="pl-7"
                  placeholder="e.g., 500"
                />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Leave empty for no minimum
              </p>
            </div>
          </div>
        </section>

        {/* Section 3: Availability */}
        <section>
          <h2 className="mb-4 text-sm font-semibold text-muted-foreground">
            AVAILABILITY
          </h2>
          <div className="space-y-4">
            <div>
              <Label>Visibility</Label>
              <div className="mt-1.5 inline-flex rounded-sm border p-1">
                <button
                  type="button"
                  className={`rounded-xs px-4 py-1.5 text-sm font-medium transition-colors ${
                    visibility === "public"
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  onClick={() => setVisibility("public")}
                >
                  Public
                </button>
                <button
                  type="button"
                  className={`rounded-xs px-4 py-1.5 text-sm font-medium transition-colors ${
                    visibility === "exclusive"
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  onClick={() => setVisibility("exclusive")}
                >
                  Exclusive
                </button>
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {visibility === "public"
                  ? "Visible on your storefront's coupon page. Anyone can collect it."
                  : "Only available via code or shared link. Hidden from the coupon page."}
              </p>
            </div>
            <div>
              <Label htmlFor="collectionLimit">Collection limit</Label>
              <Input
                id="collectionLimit"
                type="number"
                min="0"
                value={collectionLimit}
                onChange={(e) => setCollectionLimit(e.target.value)}
                className="mt-1.5"
                placeholder="e.g., 100"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                How many customers can collect this coupon? Leave empty for unlimited.
              </p>
            </div>
            <div>
              <Label htmlFor="minCartQty">Minimum cart quantity</Label>
              <Input
                id="minCartQty"
                type="number"
                min="0"
                value={minCartQty}
                onChange={(e) => setMinCartQty(e.target.value)}
                className="mt-1.5"
                placeholder="e.g., 2"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Minimum number of qualifying items in the cart. Leave empty for no minimum.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="validFrom">Valid from *</Label>
                <Input
                  id="validFrom"
                  type="date"
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="validUpto">Valid until *</Label>
                <Input
                  id="validUpto"
                  type="date"
                  value={validUpto}
                  onChange={(e) => setValidUpto(e.target.value)}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div>
              <Label>Target Customer Group</Label>
              <p className="mb-1.5 text-xs text-muted-foreground">
                Auto-issue this coupon to all customers in the selected group.
              </p>
              <DocTypeSearchAutocomplete
                doctype="Customer Group"
                value={targetCustomerGroup}
                onChange={setTargetCustomerGroup}
                onSelect={setTargetCustomerGroup}
                placeholder="Search customer group..."
                showInitialSuggestions
              />
            </div>
          </div>
        </section>

        {/* Section 4: Share (progressive disclosure) */}
        {isFormValid && (
          <section>
            <h2 className="mb-4 text-sm font-semibold text-muted-foreground">
              SHARE
            </h2>
            {/* Preview card */}
            <div className="mb-4 rounded-sm border-l-4 border-l-red-500 border bg-white p-4">
              <p className="text-xl font-bold">
                {discountType === "Amount"
                  ? `${parseInt(discountValue)} OFF`
                  : `${parseInt(discountValue)}% OFF`}
              </p>
              <p className="text-sm text-muted-foreground">{title}</p>
              <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                {minimumOrder && <span>Min. order ฿{minimumOrder}</span>}
                {minimumOrder && <span>|</span>}
                <span>
                  Ends{" "}
                  {new Date(validUpto).toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "short",
                  })}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-sm border bg-gray-50 px-4 py-3">
                <div>
                  <p className="text-xs text-muted-foreground">Coupon code</p>
                  <p className="font-mono font-medium">
                    {code.trim().toUpperCase()}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    copyToClipboard(code.trim().toUpperCase(), "code")
                  }
                >
                  {codeCopied ? (
                    <>
                      <Check className="mr-1 h-3.5 w-3.5" /> Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="mr-1 h-3.5 w-3.5" /> Copy code
                    </>
                  )}
                </Button>
              </div>

              {storefrontUrl && (
                <div className="flex items-center justify-between rounded-sm border bg-gray-50 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-muted-foreground">Share link</p>
                    <p className="truncate text-sm">{storefrontUrl}</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="ml-3 shrink-0"
                    onClick={() => copyToClipboard(storefrontUrl, "link")}
                  >
                    {linkCopied ? (
                      <>
                        <Check className="mr-1 h-3.5 w-3.5" /> Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="mr-1 h-3.5 w-3.5" /> Copy link
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Error */}
        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}

        {/* Footer actions */}
        <div className="flex items-center justify-between border-t pt-6">
          <button
            type="button"
            onClick={() => navigate("/promotions")}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
          <Button
            type="submit"
            disabled={!isFormValid || createMutation.isPending}
          >
            {createMutation.isPending ? "Creating..." : "Create Promotion"}
          </Button>
        </div>
      </form>
    </div>
  );
}
